import React, { useEffect, useRef, useState } from "react";
import { useStepNavigate } from "../hooks/useStepNavigate";
import { dataService } from "../services/dataService";
import { useOrder } from "../context/OrderContext";
import TablerLayout from "../components/layout/TablerLayout";
import FullWidthButton from "../components/buttons/FullWidthButton";
import PaginatedTable from "../components/PaginatedTable";
import FloatingLabelInput from "../components/form_elements/FloatingLabelInput";

const PartnerValaszto = () => {
    const { order, updateOrder } = useOrder();
    const navigate = useStepNavigate();
    const [partners, setPartners] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState({
        partner_name: "",
        partner_taj: order.partner?.partner_taj || "",
    });
    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState(20);
    const initialLoad = useRef(true);

    const columns = [
        { title: "Név", dataKey: "partner_name" },
        { title: "TAJ", dataKey: "partner_taj" },
        { title: "Irányítószám", dataKey: "partner_zip" },
        { title: "Város", dataKey: "partner_city" },
        { title: "Cím", dataKey: "partner_address" },
        { title: "Születési idő", dataKey: "partner_birthday" },
    ];

    const filterFields = [
        { title: "Név", dataKey: "partner_name", placeholder: "Név szerint..." },
        { title: "TAJ", inputType: "text", inputMode: "numeric", dataKey: "partner_taj", placeholder: "TAJ szerint..." },
    ];

    const fetchPartners = async (page = 1, filterObj = filters, limit = itemsPerPage) => {
        setLoading(true);
        setPartners([]);
        try {
            const offset = (page - 1) * limit;
            const filter = {};
            Object.entries(filterObj).forEach(([k, v]) => {
                if (v && v.trim() !== "") {
                    filter[k] = v;
                }
            });

            const response = await dataService.getPartners({ limit, offset, filter });

            if (response.data?.status === "ok") {
                setPartners(Array.isArray(response.data.data) ? response.data.data : []);
                setTotalCount(response.data.count);
            } else {
                setPartners([]);
                setTotalCount(0);
            }
        } catch (error) {
            setPartners([]);
            setTotalCount(0);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (initialLoad.current && order.partner?.partner_taj) {
            initialLoad.current = false;
            fetchPartners(1, filters, itemsPerPage);

            updateOrder((prev) => ({
                ...prev,
                partner: {
                    ...prev.partner,
                    partner_taj: undefined,
                },
            }));
        } else {
            fetchPartners(1, filters, itemsPerPage);
        }
    }, [itemsPerPage]);

    const handlePartnerSelect = (partner) => {
        updateOrder({ ...order, partner: { ...partner } });
        navigate("/Adat");
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        const normalizedValue =
            name === "partner_taj" ? String(value || "").replace(/\D/g, "") : value;
        const newFilters = { ...filters, [name]: normalizedValue };
        setFilters(newFilters);
        setCurrentPage(1);
        fetchPartners(1, newFilters);
    };

    const handleClearFilter = (fieldName) => {
        const newFilters = { ...filters, [fieldName]: "" };
        setFilters(newFilters);
        setCurrentPage(1);
        fetchPartners(1, newFilters);
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
        fetchPartners(page, filters);
    };

    const handleLimitChange = (newLimit) => {
        setItemsPerPage(newLimit);
        setCurrentPage(1);
        fetchPartners(1, filters, newLimit);
    };

    return (
        <TablerLayout hideHeader={true}>
            <div className="row justify-content-center">
                <div className="col-md-12 mb-4">
                    <div className="card-header d-flex flex-column flex-sm-row justify-content-between align-items-stretch align-items-sm-center gap-2">
                        <h3 className="card-title mb-0">Partner kiválasztása</h3>
                        <div className=" w-sm-auto text-center text-sm-end">
                            <FullWidthButton
                                onClick={() => {
                                    updateOrder({
                                        ...order,
                                        partner: {
                                            partner_szam: 1,
                                            partner_type: 2,
                                            partner_name: "",
                                            partner_taj: order.partner?.partner_taj || "",
                                            partner_country_szam: 2,
                                            partner_country: "",
                                            partner_zip: "",
                                            partner_city_szam: 1,
                                            partner_city: "",
                                            partner_address: "",
                                            partner_email: "",
                                            partner_phone: "",
                                            partner_birthday: "",
                                            partner_birthplace: "",
                                            partner_charitycare_num: "",
                                            partner_charity_validdate: "",
                                        },
                                    });
                                    navigate("/Adat");
                                }}
                                className="btn btn-primary"
                                style={{ fontSize: 18, padding: "8px 12px" }}
                            >
                                Új partner
                            </FullWidthButton>
                        </div>
                    </div>
                    <div className="card-body p-0">
                        <PaginatedTable 
                            columns={columns} 
                            data={partners} 
                            filterFields={filterFields} 
                            filters={filters} 
                            onFilterChange={handleFilterChange} 
                            onClearFilter={handleClearFilter}
                            onPageChange={handlePageChange} 
                            onLimitChange={handleLimitChange} 
                            onRowClick={handlePartnerSelect} 
                            itemsPerPage={itemsPerPage} 
                            currentPage={currentPage} 
                            totalCount={totalCount} 
                            rowKey={(row, idx) => (row.partner_szam ? `${row.partner_szam}_${idx}` : idx)} 
                            loading={loading} 
                            renderCustomFilter={(field) => {
                                if (field.dataKey !== "partner_name" && field.dataKey !== "partner_taj") {
                                    return null;
                                }

                                return (
                                    <div
                                        key={field.dataKey}
                                        className="filter-input-wrapper"
                                        style={{
                                            display: "inline-block",
                                            maxWidth: 220,
                                            minWidth: 120,
                                        }}
                                    >
                                        <FloatingLabelInput
                                            label={field.title}
                                            id={`filter-${field.dataKey}`}
                                            name={field.dataKey}
                                            type="text"
                                            inputMode={field.dataKey === "partner_taj" ? "numeric" : "text"}
                                            value={filters[field.dataKey] || ""}
                                            onChange={handleFilterChange}
                                            addon={filters[field.dataKey] ? "×" : null}
                                            addonOnClick={() => handleClearFilter(field.dataKey)}
                                            addonTitle="Törlés"
                                            addonAriaLabel={`${field.title} törlése`}
                                        />
                                    </div>
                                );
                            }}
                        />
                    </div>
                </div>
            </div>
        </TablerLayout>
    );
};

export default PartnerValaszto;
